import React, { useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Store } from 'redux';
import { styled } from '@compiled/react';
import { PersistGate } from 'redux-persist/integration/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AppBase from '@atlassian/jira-app-base';
import Spinner from '@atlassian/jira-common-components-spinner';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';

import { isPollinatorTenant } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/feature-flags';
import defaultStore, {
	persistor,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/store';

import BridgeEventSubscriber from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/bridge-subscriber';
import PortfolioErrorBoundary from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-boundary';
import PortfolioErrorNotification from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-notification';
import ExperienceTracker from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/experience-tracker';
import ReduxStoreLifecycle from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/redux-store-lifecycle';
import type { InjectedProps } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/experience-tracking';
import { useRouter, useResource } from '@atlassian/jira-router';
import {
	advancedRoadmapsPlanPageInitialChecksResource,
	advancedRoadmapsPlanReportPageInitialChecksResource,
} from '@atlassian/jira-router-resources-advanced-roadmaps-plan';
import { JIRA_PORTFOLIO } from '@atlassian/jira-shared-types';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product';
import { RedirectToLoginPage } from '@atlassian/jira-spa-apps-common/src/redirect/login';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller';
import { useLocale, useAppEditions } from '@atlassian/jira-tenant-context-controller';

import { ErrorScreen } from '../../../index';
import { AnalyticsInitializer } from './analytics-initializer';
import { usePlanCheck } from './utils';

interface Props {
	/** Passes the id of the app */
	appKey?: string;

	/** Provides the app with the default ARJ Redux store. */
	withReduxStore?: boolean;

	/** Provides the default left padding to the container. */
	paddingLeft?: boolean;

	/** Provides the default right padding to the container. */
	paddingRight?: boolean;

	children?: ReactNode | ReactNode[];

	/** Provides a custom Redux store. */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	store?: Store<any>;

	/** Provides the view name to allow store to set active view */
	planView?: 'timeline' | 'calendar';
}

export const ReportAppBase = ({
	withReduxStore,
	paddingLeft = true,
	paddingRight = true,
	children,
	store = defaultStore,
}: Props & InjectedProps) => {
	const locale: Locale = useLocale();
	const editions = useAppEditions();

	const { data, loading, error } = useResource(advancedRoadmapsPlanReportPageInitialChecksResource);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		return <ErrorScreen errorMessage={data.errorMessage} planId={data.planId} />;
	}

	return (
		<JSErrorBoundary
			id="spa-app.advanced-roadmaps-plan.report"
			packageName="jiraSpaAppsAdvancedRoadmaps"
			teamName="advanced-roadmaps"
		>
			<MarkProductStart />
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={editions.software}
				additionalAttributes={{
					synthetic: isPollinatorTenant(),
				}}
			>
				<SubProductUpdater subProduct="software" />
				<AppBase store={withReduxStore ? store : undefined} hasErrorBoundary={false}>
					<AnalyticsInitializer>
						<PortfolioErrorBoundary>
							{document.body && createPortal(<PortfolioErrorNotification />, document.body)}
							<PersistGate loading={null} persistor={persistor}>
								<ReduxStoreLifecycle locale={locale} />
								<Container
									paddingLeft={paddingLeft}
									paddingRight={paddingRight}
									// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className="arj"
								>
									{children}
								</Container>
								<SpaStatePageReady />
							</PersistGate>

							<ExperienceTracker />
						</PortfolioErrorBoundary>
					</AnalyticsInitializer>
				</AppBase>
			</ViewExperienceTrackingProvider>
		</JSErrorBoundary>
	);
};

export const PlanAppBase = ({
	appKey = 'spa-app.advanced-roadmaps-plan.default',
	withReduxStore,
	paddingLeft = true,
	paddingRight = true,
	children,
	store = defaultStore,
	planView,
}: Props & InjectedProps) => {
	const locale: Locale = useLocale();
	const editions = useAppEditions();
	const { data, loading, error } = usePlanCheck();
	const [{ match }] = useRouter();

	const containerStyles = useMemo(() => {
		const styles = [containerStylesNoPadding];
		if (paddingLeft) styles.push(containerWithPaddingLeft);
		if (paddingRight) styles.push(containerWithPaddingRight);
		return styles;
	}, [paddingLeft, paddingRight]);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		const planId = Number.isNaN(match.params.planId) ? undefined : Number(match.params.planId);
		return <ErrorScreen errorMessage={data.errorMessage} planId={planId} />;
	}

	return (
		<JSErrorBoundary
			id={appKey}
			packageName="jiraSpaAppsAdvancedRoadmaps"
			teamName="advanced-roadmaps"
		>
			<MarkProductStart />
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={editions.software}
				additionalAttributes={{
					synthetic: isPollinatorTenant(),
				}}
			>
				<SubProductUpdater subProduct="software" />
				<AppBase store={withReduxStore ? store : undefined} hasErrorBoundary={false}>
					<AnalyticsInitializer>
						<PortfolioErrorBoundary>
							{document.body && createPortal(<PortfolioErrorNotification />, document.body)}
							<PersistGate loading={null} persistor={persistor}>
								<ReduxStoreLifecycle locale={locale} planView={planView} />
								{ff('jet-plans-nav-refresh_f9025') ? (
									<Box xcss={containerStyles}>{children}</Box>
								) : (
									<Container
										paddingLeft={paddingLeft}
										paddingRight={paddingRight}
										// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className="arj"
									>
										{children}
									</Container>
								)}
								<SpaStatePageReady />
							</PersistGate>
							<ExperienceTracker />
							{ff('com.atlassian.rm.jpo.jpo3cloud.increment-planning-board-m1') && (
								<BridgeEventSubscriber />
							)}
						</PortfolioErrorBoundary>
					</AnalyticsInitializer>
				</AppBase>
			</ViewExperienceTrackingProvider>
		</JSErrorBoundary>
	);
};

export const SettingsAppBase = ({
	children,
	appKey = 'spa-app.advanced-roadmaps-plan.settings',
	store = defaultStore,
}: Props) => {
	const locale: Locale = useLocale();
	const editions = useAppEditions();
	const [{ match }] = useRouter();

	const { data, loading, error } = useResource(advancedRoadmapsPlanPageInitialChecksResource);

	// @ts-expect-error - TS2339 - Property 'statusCode' does not exist on type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)'.
	if (error?.statusCode === 401) {
		return <RedirectToLoginPage />;
	}

	if (loading || data === null) {
		return <Spinner />;
	}

	if (!data.success) {
		const planId = Number.isNaN(match.params.planId) ? undefined : Number(match.params.planId);
		return <ErrorScreen errorMessage={data.errorMessage} planId={planId} />;
	}
	return (
		<JSErrorBoundary
			id={appKey}
			packageName="jiraSpaAppsAdvancedRoadmaps"
			teamName="advanced-roadmaps"
		>
			<MarkProductStart />
			<ViewExperienceTrackingProvider
				experience="viewPortfolio3PlanSettings"
				analyticsSource="portfolio3"
				application={JIRA_PORTFOLIO}
				edition={editions.software}
				additionalAttributes={{
					synthetic: isPollinatorTenant(),
				}}
			>
				<SubProductUpdater subProduct="software" />
				<AppBase store={store} hasErrorBoundary={false}>
					<AnalyticsInitializer>
						<PortfolioErrorBoundary>
							{document.body && createPortal(<PortfolioErrorNotification />, document.body)}
							<PersistGate loading={null} persistor={persistor}>
								<ReduxStoreLifecycle locale={locale} />
								<Container
									paddingLeft
									paddingRight
									// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className="arj"
								>
									{children}
								</Container>
								<SpaStatePageReady />
							</PersistGate>
							<ExperienceTracker />
						</PortfolioErrorBoundary>
					</AnalyticsInitializer>
				</AppBase>
			</ViewExperienceTrackingProvider>
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ paddingLeft: boolean; paddingRight: boolean }>({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({ paddingLeft }) => (paddingLeft ? token('space.500', '40px') : undefined),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ paddingRight }) => (paddingRight ? token('space.300', '24px') : undefined),
	height: '100%',
});

const containerStylesNoPadding = xcss({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
});
const containerWithPaddingLeft = xcss({
	paddingLeft: 'space.300',
});
const containerWithPaddingRight = xcss({
	paddingRight: 'space.300',
});
